import {
  USER_CRM_REQUEST,
  USER_CRM_SUCCESS,
  USER_CRM_ERROR,
  USER_SHOW_TOS_DIALOG,
  USER_HIDE_TOS_DIALOG,
  USER_SHOW_TOS_DIALOG_PARTNER,
  USER_HIDE_TOS_DIALOG_PARTNER,
  USER_SHOW_PENDING_INVITE_DIALOG,
  USER_HIDE_PENDING_INVITE_DIALOG,
  USER_CHECK_PENDING_INVITE_DIALOG,
  USER_CHECK_TAC_TOS_DIALOG,
  USER_ACCEPTED_TOS,
  USER_KNOWLEDGEBASE_REQUEST,
  USER_KNOWLEDGEBASE_SUCCESS,
  USER_KNOWLEDGEBASE_ERROR,
  USER_SET_ROUTE,
  USER_SET_GLOBAL_NOTIFICATIONS,
  USER_CRM_LICENCE_STATUS_REQUEST,
  USER_CRM_LICENCE_STATUS_SUCCESS,
  USER_CRM_LICENCE_STATUS_ERROR,
  USER_CRM_LICENCE_STATUS_REQUIRED,
  USER_CRM_RELOAD,
} from "@/store/actions/user";
import { SOCKET_UPDATE_NOTIFIACTIONS_BADGE } from "@/store/actions/sockets";
import {
  SNACKBAR,
  SNACKBAR_GLOBAL_NOTIFICATIONS,
} from "@/store/actions/snackbar";
import api from "@/utils/api";
import Vue from "vue";
import { AUTH_LOGOUT } from "@/store/actions/auth";
import router from "../../router";
import restrictions from "@/utils/restrictions";
import i18n from "@/utils/translations";

export const getDefaultState = () => ({
  user: {},
  tosAccepted: true,
  tosPartnerAccepted: true,
  knowledgeBase: null,
  pendingInviteAccepted: true,
  route: null,
  globalNotifications: [],
  licenceStatus: {
    fetched: false,
    required: false,
    dealerships: [],
  },
});

const getAvailableLicenceStatuses = () => {
  return [
    { status: "success", restrictedAccess: false },
    { status: "warning", restrictedAccess: false },
    { status: "error", restrictedAccess: true },
  ];
};

const state = getDefaultState();

const getters = {
  getMe: (state) => state.user,
  getTosAccepted: (state) => state.tosAccepted,
  getTosPartnerAccepted: (state) => state.tosPartnerAccepted,
  getKnowledgeBase: (state) => state.knowledgeBase,
  getRoute: (state) => state.route,
  getGlobalNotifications: (state) => state.globalNotifications,
  getCurrentLicence: (state) => state.licenceStatus,

  getLicenceStatus: (state) => {
    if (state.licenceStatus.dealerships.length === 0) {
      return null;
    }
    const status = state.licenceStatus.dealerships.every(
      (dealer) =>
        dealer.subscription.status === "success" ||
        dealer.subscription.status === "warning"
    );

    //NOTE: This can potentially cause issues!
    //for us in the frontend it doesn't matter whether has the NO_PLAN or UNPAID_PLAN
    return status ? "success" : "error";
  },
  getIsLicenceRequired: (state) => state.licenceStatus.required,
  getNonValidDealerships: (state) => {
    const restrictedAccessLicenceStatuses = getAvailableLicenceStatuses()
      .filter((status) => status.restrictedAccess)
      .map((status) => status.status);

    return state.licenceStatus.dealerships.filter((dealership) => {
      return restrictedAccessLicenceStatuses.includes(dealership.status);
    });
  },
  getHasCurrentLicenceStatusRestrictedAccess: (state, getters) => {
    const currentLicenceStatus = getters.getCurrentLicenceStatus;

    if (currentLicenceStatus == null) {
      console.error("RESTRICTIONS: Licence status not present");
      return false;
    }

    return currentLicenceStatus.restrictedAccess;
  },
  getCurrentLicenceStatus: (state, getters) => {
    const currentLicenceStatus = getters.getLicenceStatus;
    const availableLicenceStatuses = getAvailableLicenceStatuses();

    const licenceStatus = availableLicenceStatuses.find(
      (licenceStatus) => licenceStatus.status === currentLicenceStatus
    );

    if (!getters.getIsLicenceRequired) {
      return false;
    }

    //TODO: Handle this error case properly
    if (licenceStatus == null) {
      console.error("RESTRICTIONS: Licence status not present");
      return;
    }

    return licenceStatus;
  },
  getIsUIDisabledBasedOnLicenceStatus: (state, getters) => {
    if (!getters.getIsLicenceStatusFetched && !getters.getIsLicenceRequired) {
      return false;
    }

    if (!getters.getIsLicenceStatusFetched && getters.getIsLicenceRequired) {
      console.error(
        "RESTRICTION: Licence status is accessed before beeing fetched"
      );
      return true;
    }

    return getters.getHasCurrentLicenceStatusRestrictedAccess;
  },
  getIsLicenceStatusNeeded: (state, getters) => {
    return getters.getBrandID === "beneteau";
  },
  getIsLicenceStatusFetched: (state, getters) => {
    const licenceStatus = state.licenceStatus;

    if (!licenceStatus.fetched) {
      return false;
    }

    if (!getters.getIsLicenceStatusNeeded) {
      return true;
    }

    //NOTE: This is the case when the API returns the status is_external_api_online: false
    if (licenceStatus.fetched && !licenceStatus.required) {
      return true;
    }

    if (licenceStatus.fetched && getters.getLicenceStatus === null) {
      console.error(
        "RESTRICTIONS: Licence status is fetched but status is not set"
      );
      return false;
    }

    const licenceStatuses = getAvailableLicenceStatuses().map(
      (item) => item.status
    );

    if (!licenceStatuses.includes(getters.getLicenceStatus)) {
      console.error(
        "RESTRICTIONS: Licence status is fetched but status does not exist"
      );
      return false;
    }
    return true;
  },
  getRouteBasedOnLicenceStatus: (state, getters) => {
    const licenceStatus = getters.getLicenceStatus;

    if (licenceStatus === "error") {
      return { name: "dashboards" };
    }

    if (licenceStatus === "success" || licenceStatus === "warning") {
      // NOTE: This return present as continue down to logic
      return {};
    }

    return null;
  },
};

const actions = {
  [USER_CRM_RELOAD]: ({ commit, dispatch, getters }) => {
    return new Promise((resolve, reject) => {
      dispatch(USER_CRM_REQUEST)
        .then((response) => {
          const isLicenceStatusNeeded = getters.getIsLicenceStatusNeeded;
          const hasNoDealerships = response.dealerships.length === 0;
          const hasInternalDealer = response.dealerships.some(
            (dealer) => dealer.is_internal
          );

          if (!isLicenceStatusNeeded || hasNoDealerships || hasInternalDealer) {
            commit(USER_CRM_LICENCE_STATUS_REQUIRED, false);
            return resolve();
          }

          dispatch(USER_CRM_LICENCE_STATUS_REQUEST, response.dealerships).then(
            (resp) => {
              resolve();
            }
          );
        })
        .catch((err) => {
          reject();
          console.error(err);
        });
    });
  },
  [USER_ACCEPTED_TOS]: ({ commit, dispatch }) => {
    return api
      .acceptTOS()
      .then((resp) => {
        commit(USER_HIDE_TOS_DIALOG);
      })
      .catch((resp) => {
        dispatch(AUTH_LOGOUT);
      });
  },
  [USER_CRM_LICENCE_STATUS_REQUEST]: (
    { commit, dispatch, getters },
    dealerships
  ) => {
    // const findMessageBasedOnLocale = (messages) => {
    //   const locale = i18n.locale;

    //   for (const messageLocale in messages) {
    //     if (Object.hasOwnProperty.call(messageLocale, locale))
    //       return messages[messageLocale];
    //   }

    //   return messages["en"];
    // };

    return new Promise((resolve, reject) => {
      const promises = dealerships.map((dealership) => {
        return api
          .getCrmLicence(dealership.uuid, !getters.getCurrentLicence.fetched)
          .then((resp) => {
            return {
              ...resp.data,
              id: dealership.uuid,
            };
          });
      });

      Promise.all(promises)
        .then((values) => {
          if (values.some((item) => !item.is_external_api_online)) {
            commit(USER_CRM_LICENCE_STATUS_REQUIRED, false);
            resolve();
            return;
          }

          commit(USER_CRM_LICENCE_STATUS_SUCCESS, values);
          values
            .filter((item) => {
              return item.subscription.message != null;
            })
            .map((item) => {
              return item.subscription.message;
            })
            .forEach((message) => {
              commit(SNACKBAR, {
                active: true,
                text: message,
                timeout: 0,
                color: "primary",
                globalNotifications: {},
              });
            });
          resolve();
        })
        .catch((err) => {
          commit(USER_CRM_LICENCE_STATUS_REQUIRED, false);
          resolve();
          // console.error(err);
          // commit(USER_CRM_LICENCE_STATUS_ERROR);
          // dispatch(AUTH_LOGOUT);
          // //TODO: MAKE DIFFERENT PAGE SOMETHING LIKE UNDERMAINTENANCE
          // router.push({
          //   name: "noPermission",
          // });
          // reject();
        });
    });
  },
  [USER_CRM_REQUEST]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      api
        .getCrmUser()
        .then((resp) => {
          commit(USER_CRM_SUCCESS, resp.data);
          commit(SOCKET_UPDATE_NOTIFIACTIONS_BADGE, {
            badge_count: resp.data.menu_access.notifications.badge,
          });

          if (
            typeof resp.data.user.profile.preferred_language !== "undefined" &&
            resp.data.user.profile.preferred_language === null
          ) {
            api.setPreferredLanguage({
              preferred_language:
                window.navigator.userLanguage || window.navigator.language,
            });
          }

          if (
            typeof resp.data.global_notifications !== "undefined" &&
            resp.data.global_notifications.length !== 0
          ) {
            dispatch(
              SNACKBAR_GLOBAL_NOTIFICATIONS,
              resp.data.global_notifications
            );

            commit(
              USER_SET_GLOBAL_NOTIFICATIONS,
              resp.data.global_notifications
            );
          }

          dispatch(USER_CHECK_TAC_TOS_DIALOG, resp).then((accepted) => {
            if (!accepted.tosAccepted && !accepted.tosPartnerAccepted) {
              commit(USER_SHOW_TOS_DIALOG_PARTNER);
              resolve(resp.data);
              return;
            }

            if (accepted.tosAccepted && !accepted.tosPartnerAccepted) {
              commit(USER_SHOW_TOS_DIALOG_PARTNER);
              resolve(resp.data);
              return;
            }

            if (!accepted.tosAccepted && accepted.tosPartnerAccepted) {
              commit(USER_SHOW_TOS_DIALOG);
              resolve(resp.data);
              return;
            }

            if (accepted.tosAccepted && accepted.tosPartnerAccepted) {
              dispatch(USER_CHECK_PENDING_INVITE_DIALOG, resp.data)
                .then(() => {
                  commit(USER_SHOW_PENDING_INVITE_DIALOG);
                  resolve(resp.data);
                })
                .catch(() => {
                  commit(USER_HIDE_PENDING_INVITE_DIALOG);
                  commit(USER_HIDE_TOS_DIALOG_PARTNER);
                  resolve(resp.data);
                });
            }
          });
        })
        .catch((resp) => {
          commit(USER_CRM_ERROR);
          dispatch(AUTH_LOGOUT);
          router.push({
            name: "noPermission",
          });
          reject();
        });
    });
  },

  [USER_CHECK_PENDING_INVITE_DIALOG]: (
    { commit, dispatch, getters },
    payload
  ) => {
    return new Promise((resolve, reject) => {
      if (
        ["termsOfService", "register", "login", null].includes(
          getters.getRoute
        ) &&
        payload.pending_invites.length === 0
      ) {
        reject();
      }
      resolve();
    });
  },

  [USER_CHECK_TAC_TOS_DIALOG]: ({ commit, dispatch }, resp) => {
    return new Promise((resolve, reject) => {
      let isDealershipComplete = (dealership) => {
        return (
          dealership.is_profile_complete &&
          dealership.pp_template_accept_date !== null &&
          dealership.tac_accept_date !== null
        );
      };

      //nondealer user
      if (
        resp.data.user.profile.TOS_accept_date === null &&
        resp.data.dealerships.length === 0
      ) {
        resolve({ tosAccepted: false, tosPartnerAccepted: true });
      }

      //dealeruser without simple TOS accepted
      if (resp.data.dealerships.length !== 0) {
        let dealerships = resp.data.dealerships.filter(
          (dealership) => dealership.has_onboarding_feature
        );

        dealerships = dealerships.map((dealership) => {
          return {
            ...dealership,
            isComplete: isDealershipComplete(dealership),
          };
        });

        let dealershipIndex = dealerships.findIndex((dealership) => {
          return !dealership.isComplete;
        });

        if (dealershipIndex === -1) {
          if (resp.data.user.profile.TOS_accept_date === null) {
            resolve({ tosAccepted: false, tosPartnerAccepted: true });
          } else {
            resolve({ tosAccepted: true, tosPartnerAccepted: true });
          }
        } else {
          resolve({ tosAccepted: false, tosPartnerAccepted: false });
        }
      }

      resolve({ tosAccepted: true, tosPartnerAccepted: true });
    });
  },

  [USER_KNOWLEDGEBASE_REQUEST]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      api
        .getKnowledgeBase()
        .then((resp) => {
          commit(USER_KNOWLEDGEBASE_SUCCESS, resp.data);
          resolve(resp.data);
        })
        .catch((resp) => {
          commit(USER_KNOWLEDGEBASE_ERROR);
          reject();
        });
    });
  },
};

const mutations = {
  [USER_SET_ROUTE]: (state, route) => {
    state.route = route;
  },
  [USER_SHOW_TOS_DIALOG]: (state, resp) => {
    state.tosAccepted = false;
  },
  [USER_HIDE_TOS_DIALOG]: (state, resp) => {
    state.tosAccepted = true;
  },
  [USER_SHOW_TOS_DIALOG_PARTNER]: (state, resp) => {
    state.tosPartnerAccepted = false;
  },
  [USER_HIDE_TOS_DIALOG_PARTNER]: (state, resp) => {
    state.tosPartnerAccepted = true;
  },
  [USER_SHOW_PENDING_INVITE_DIALOG]: (state, resp) => {
    state.pendingInviteAccepted = false;
  },
  [USER_HIDE_PENDING_INVITE_DIALOG]: (state, resp) => {
    state.pendingInviteAccepted = true;
  },
  [USER_CRM_SUCCESS]: (state, resp) => {
    Vue.set(state, "user", resp);
  },
  [USER_KNOWLEDGEBASE_SUCCESS]: (state, resp) => {
    Vue.set(state, "knowledgeBase", resp);
  },
  [USER_KNOWLEDGEBASE_ERROR]: (state, resp) => {
    Vue.set(state, "knowledgeBase", { error: true });
  },
  [USER_SET_GLOBAL_NOTIFICATIONS]: (state, resp) => {
    state.globalNotifications = resp;
  },
  [USER_CRM_LICENCE_STATUS_REQUIRED]: (state, required) => {
    state.licenceStatus = {
      ...state.licenceStatus,
      required: required,
      fetched: true,
    };
  },
  [USER_CRM_LICENCE_STATUS_SUCCESS]: (state, resp) => {
    state.licenceStatus = {
      ...state.licenceStatus,
      dealerships: resp,
      fetched: true,
      required: true,
    };
  },
  [USER_CRM_LICENCE_STATUS_ERROR]: (state, resp) => {
    state.licenceStatus = {
      ...state.licenceStatus,
      fetched: true,
      status: null,
      dealerships: [],
      required: true,
    };
  },
  [AUTH_LOGOUT]: (state) => {
    Object.assign(state, {
      status: "",
      tosAccepted: true,
      tosPartnerAccepted: true,
      pendingInviteAccepted: true,
      knowledgeBase: [],
      route: null,
    });
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
